// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { useState, useCallback, useRef, useEffect } from "react";
import AutosizeInput from "react-input-autosize";
import * as R from "ramda";

import { EditTitle as StyledEditTitle, OptionalTitle } from "./styles";
import Title from "./Medium";
import WorkflowName from "src/containers/workflow/Name";
import Privacy from "src/components/chatroom/Privacy";
import OutsideClickHandler from "src/components/OutsideClickHandler";
import Icon from "src/components/Icon";
import { iconSize } from "src/styles/constants/size";
import * as colors from "src/styles/constants/colors";

import type { RoomId } from "src/types";

type Props = {
  type: string,
  title: string,
  roomId: RoomId,
  seqNo: number,
  canceled: ?boolean,
  templateId?: ?number,
  setAttribute: Function,
};

const EditTitle = ({
  type,
  templateId = null,
  seqNo,
  canceled,
  title,
  setAttribute,
  roomId,
}: Props) => {
  const outerRef = useRef(null);
  const [edit, setEdit] = useState(false);
  const [newTitle, setTitle] = useState("");

  const handleEdit = useCallback(() => {
    setTitle(title);
    setEdit((prevState) => !prevState);
  }, [title, setTitle, setEdit]);

  const resetTitle = useCallback(() => {
    setEdit(false);
    setTitle("");
  }, [setEdit, setTitle]);

  useEffect(() => {
    resetTitle();
  }, [roomId, resetTitle]);

  const handleSubmit = useCallback(
    (e: any) => {
      e.preventDefault();
      if (R.trim(title || "") !== R.trim(newTitle || "")) {
        setAttribute(roomId, { title: R.trim(newTitle || "") });
      }
      resetTitle();
    },
    [setAttribute, roomId, title, newTitle, resetTitle],
  );

  const handleKeyPress = useCallback(
    (e: any) => {
      // handle ESC
      if (e.keyCode === 27) {
        resetTitle();
      }
    },
    [resetTitle],
  );

  const handleChange = useCallback(
    (e: any) => {
      const { value } = e.target;
      setTitle(value);
    },
    [setTitle],
  );

  if (!edit) {
    return (
      <div
        onClick={handleEdit}
        onKeyPress={handleEdit}
        role="button"
        tabIndex="0"
      >
        <Title
          type={type}
          title={title}
          templateId={templateId}
          seqNo={seqNo}
          roomId={roomId}
          canceled={canceled}
        />
      </div>
    );
  }

  return (
    <OutsideClickHandler onClickOutside={handleSubmit}>
      <div>
        <StyledEditTitle
          ref={outerRef}
          onSubmit={handleSubmit}
          canceled={canceled}
        >
          <Icon icon={type} color={colors.secondary} size={iconSize.space_l} />

          <h4 title={title}>
            <Privacy roomId={roomId} />
            {templateId && type === "workflow" ? (
              <>
                <WorkflowName id={templateId} />{" "}
                {seqNo && `${i18n.t(k._7)}${seqNo}`}
                {i18n.t(k._)}
              </>
            ) : null}
            <OptionalTitle>
              <AutosizeInput
                type="text"
                value={newTitle}
                onKeyDown={handleKeyPress}
                onChange={handleChange}
                autoFocus
              />
            </OptionalTitle>
          </h4>
        </StyledEditTitle>
      </div>
    </OutsideClickHandler>
  );
};

export default EditTitle;
